import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CardAccount,
  CardAccountsResponse,
  CardAccountStats,
  CardAccountFilters,
  ENVIRONMENT
} from 'shared';

@Injectable({
  providedIn: 'root'
})
export class CardAccountService {
  private readonly apiUrl = this.environment.apiUrl || 'http://localhost:3000/api';

  constructor(private http: HttpClient, @Inject(ENVIRONMENT) private environment: any) {}

  /**
   * Get paginated and filtered list of card accounts
   */
  getCardAccounts(filters?: CardAccountFilters): Observable<CardAccountsResponse> {
    let params = new HttpParams();

    if (filters) {
      if (filters.filter_user_id) {
        params = params.set('filter_user_id', filters.filter_user_id.toString());
      }
      if (filters.filter_card_type) {
        params = params.set('filter_card_type', filters.filter_card_type);
      }
      if (filters.filter_card_number) {
        params = params.set('filter_card_number', filters.filter_card_number.toString());
      }
      if (filters.filter_balance_min !== undefined) {
        params = params.set('filter_balance_min', filters.filter_balance_min.toString());
      }
      if (filters.filter_balance_max !== undefined) {
        params = params.set('filter_balance_max', filters.filter_balance_max.toString());
      }
      if (filters.filter_turnover_min !== undefined) {
        params = params.set('filter_turnover_min', filters.filter_turnover_min.toString());
      }
      if (filters.filter_turnover_max !== undefined) {
        params = params.set('filter_turnover_max', filters.filter_turnover_max.toString());
      }
      if (filters.filter_initial_payment) {
        params = params.set('filter_initial_payment', filters.filter_initial_payment);
      }
      if (filters.filter_is_beta !== undefined) {
        params = params.set('filter_is_beta', filters.filter_is_beta.toString());
      }
      if (filters.filter_user_search) {
        params = params.set('filter_user_search', filters.filter_user_search);
      }
      if (filters.sort_column) {
        params = params.set('sort_column', filters.sort_column);
      }
      if (filters.sort_direction) {
        params = params.set('sort_direction', filters.sort_direction);
      }
      if (filters.page) {
        params = params.set('page', filters.page.toString());
      }
      if (filters.per_page) {
        params = params.set('per_page', filters.per_page.toString());
      }
    }

    return this.http.get<CardAccountsResponse>(`${this.apiUrl}/card_accounts`, { params });
  }

  /**
   * Get single card account by ID
   */
  getCardAccount(id: number): Observable<CardAccount> {
    return this.http.get<CardAccount>(`${this.apiUrl}/card_accounts/${id}`);
  }

  /**
   * Get all card accounts for a specific user
   */
  getCardAccountsByUser(userId: number): Observable<{ card_accounts: CardAccount[] }> {
    return this.http.get<{ card_accounts: CardAccount[] }>(
      `${this.apiUrl}/card_accounts/by_user/${userId}`
    );
  }

  /**
   * Get card account statistics
   */
  getStats(): Observable<CardAccountStats> {
    return this.http.get<CardAccountStats>(`${this.apiUrl}/card_accounts/stats`);
  }

  /**
   * Get current user's own card accounts (no admin required)
   */
  getMyCardAccounts(): Observable<{ card_accounts: CardAccount[] }> {
    return this.http.get<{ card_accounts: CardAccount[] }>(`${this.apiUrl}/my_card_accounts`);
  }

  /**
   * Irreversibly mark a card account as beta (user's own cards only)
   */
  activateBeta(cardAccountId: number): Observable<CardAccount> {
    return this.http.post<CardAccount>(
      `${this.apiUrl}/my_card_accounts/${cardAccountId}/activate_beta`, {}
    );
  }
}
